import { useEffect, useRef, useState } from 'react';
import { Loader2, MapPin } from 'lucide-react';
import * as maptilersdk from '@maptiler/sdk';
import '@maptiler/sdk/dist/maptiler-sdk.css';
import {
  ABEOKUTA_CENTER,
  MAPTILER_API_KEY,
  MAPTILER_NAV_STYLE_URL,
  MAPTILER_STYLE_URL,
} from '../../utils/maptilerConfig';
import { fetchInAppRoute, shouldRefetchInAppRoute } from '../../utils/orsRouting';
import {
  closestPointIndex,
  distanceMeters,
  formatDistance,
  formatEta,
  type LatLng,
} from '../../utils/locationSmoothing';
import {
  createCameraState,
  lerpHeading,
  type CameraState,
} from '../../utils/mapCamera';

export interface MapLocation {
  lat: number;
  lng: number;
  address?: string;
}

export interface NearbyJobMarker {
  id: string;
  lat: number;
  lng: number;
  label?: string;
}

export interface RouteStats {
  distanceMeters: number;
  durationMinutes: number;
  distanceLabel: string;
  etaLabel: string;
}

interface MapTilerNavMapProps {
  pickupLocation?: MapLocation;
  deliveryLocation?: MapLocation;
  currentLocation?: MapLocation;
  nearbyJobs?: NearbyJobMarker[];
  onRouteStatsUpdate?: (stats: RouteStats) => void;
  showRoute?: boolean;
  compact?: boolean;
  zoom?: number;
  mode?: 'default' | 'navigation';
  navTarget?: MapLocation;
  premiumNav?: boolean;
  followRider?: boolean;
  riderHeading?: number;
  isRiderMoving?: boolean;
  hasRiderGps?: boolean;
}

function makeDotMarkerEl(color: string, size = 28): HTMLDivElement {
  const el = document.createElement('div');
  el.style.width = `${size}px`;
  el.style.height = `${size}px`;
  el.style.borderRadius = '9999px';
  el.style.background = color;
  el.style.border = '3px solid #fff';
  el.style.boxShadow = '0 2px 8px rgba(0,0,0,0.35)';
  return el;
}

function makeRiderEl(heading = 0): HTMLDivElement {
  const el = document.createElement('div');
  el.style.width = '44px';
  el.style.height = '44px';
  el.style.borderRadius = '9999px';
  el.style.background = '#111111';
  el.style.border = '3px solid #E0001B';
  el.style.boxShadow = '0 2px 10px rgba(0,0,0,0.4)';
  el.style.display = 'flex';
  el.style.alignItems = 'center';
  el.style.justifyContent = 'center';
  el.style.transform = `rotate(${heading}deg)`;
  el.style.willChange = 'transform';
  el.innerHTML =
    '<div style="width:0;height:0;border-left:7px solid transparent;border-right:7px solid transparent;border-bottom:14px solid #fff;margin-top:-4px"></div>';
  return el;
}

function setRiderHeading(el: HTMLElement, heading: number) {
  el.style.transform = `rotate(${heading}deg)`;
}

/**
 * In-app MapTiler map. Navigation mode uses streets-v2 so roads/labels show,
 * camera follows rider motion, route stays on streets via Mapbox/ORS.
 * Display-only — does not change order status.
 */
export function MapTilerNavMap({
  pickupLocation,
  deliveryLocation,
  currentLocation,
  nearbyJobs = [],
  onRouteStatsUpdate,
  showRoute = true,
  compact = false,
  zoom = 14,
  mode = 'default',
  navTarget,
  premiumNav = false,
  followRider = false,
  riderHeading = 0,
  isRiderMoving = false,
  hasRiderGps = true,
}: MapTilerNavMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maptilersdk.Map | null>(null);
  const markersRef = useRef<Record<string, maptilersdk.Marker>>({});
  const routePathRef = useRef<LatLng[]>([]);
  const lastNavOriginRef = useRef<LatLng | null>(null);
  const lastNavFetchAtRef = useRef(0);
  const navDestKeyRef = useRef('');
  const livePosRef = useRef<LatLng | null>(null);
  const liveHeadingRef = useRef(0);
  const isMovingRef = useRef(false);
  const cameraStateRef = useRef<CameraState | null>(null);
  const lastCameraTsRef = useRef(0);
  const lastRouteProgressRef = useRef(0);
  const onStatsRef = useRef(onRouteStatsUpdate);
  onStatsRef.current = onRouteStatsUpdate;
  const [mapReady, setMapReady] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const isNavMode = mode === 'navigation' || premiumNav;

  // HARD FOLLOW on MapTiler backup — every GPS update
  useEffect(() => {
    if (!mapReady || !isNavMode || !currentLocation?.lat) return;
    const map = mapRef.current;
    if (!map) return;
    const pos = { lat: currentLocation.lat, lng: currentLocation.lng };
    livePosRef.current = pos;
    liveHeadingRef.current = riderHeading || 0;
    isMovingRef.current = isRiderMoving;
    try {
      map.setCenter([pos.lng, pos.lat]);
      if (map.getZoom() < 15) map.setZoom(16);
      const existing = markersRef.current['rider'];
      if (existing) {
        existing.setLngLat([pos.lng, pos.lat]);
        setRiderHeading(existing.getElement(), riderHeading || 0);
      } else {
        markersRef.current['rider'] = new maptilersdk.Marker({
          element: makeRiderEl(riderHeading || 0),
        })
          .setLngLat([pos.lng, pos.lat])
          .addTo(map);
      }
      if (routePathRef.current.length > 1) {
        drawRoute(routePathRef.current, pos);
      }
    } catch (e) {
      console.warn('[maptiler] hard follow failed:', e);
    }
  }, [
    mapReady,
    isNavMode,
    currentLocation?.lat,
    currentLocation?.lng,
    riderHeading,
    isRiderMoving,
  ]);

  // Keep live GPS refs hot without restarting camera loops
  useEffect(() => {
    if (currentLocation?.lat && currentLocation?.lng) {
      livePosRef.current = { lat: currentLocation.lat, lng: currentLocation.lng };
      if (!cameraStateRef.current) {
        cameraStateRef.current = createCameraState(livePosRef.current);
      }
    }
    liveHeadingRef.current = riderHeading || 0;
    isMovingRef.current = isRiderMoving;
  }, [
    currentLocation?.lat,
    currentLocation?.lng,
    riderHeading,
    isRiderMoving,
  ]);

  // Init MapTiler — streets style for en-route so roads + names are visible
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    maptilersdk.config.apiKey = MAPTILER_API_KEY;
    let cancelled = false;

    try {
      const center = currentLocation || pickupLocation || ABEOKUTA_CENTER;
      const map = new maptilersdk.Map({
        container: containerRef.current,
        style: isNavMode ? MAPTILER_NAV_STYLE_URL : MAPTILER_STYLE_URL,
        center: [center.lng, center.lat],
        zoom: isNavMode ? Math.max(zoom, 16) : zoom,
        maxZoom: 19,
        minZoom: 10,
        pitch: isNavMode ? 50 : 0,
        bearing: 0,
        attributionControl: { compact: true },
      });

      map.on('load', () => {
        if (cancelled) return;
        mapRef.current = map;
        setMapReady(true);
        map.resize();
      });

      map.on('error', () => {
        console.error('MapTiler map error');
        setLoadError(true);
      });
    } catch (err) {
      console.error('MapTiler init failed:', err);
      setLoadError(true);
    }

    return () => {
      cancelled = true;
      Object.values(markersRef.current).forEach((m) => m.remove());
      markersRef.current = {};
      mapRef.current?.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init once
  }, []);

  // Resize when container changes
  useEffect(() => {
    if (!mapReady || !mapRef.current || !containerRef.current) return;
    const map = mapRef.current;
    const ro = new ResizeObserver(() => map.resize());
    ro.observe(containerRef.current);
    map.resize();
    return () => ro.disconnect();
  }, [mapReady]);

  const upsertMarker = (key: string, pos: LatLng, el: HTMLElement) => {
    const map = mapRef.current;
    if (!map) return;
    const existing = markersRef.current[key];
    if (existing) {
      existing.setLngLat([pos.lng, pos.lat]);
      if (key === 'rider') setRiderHeading(existing.getElement(), liveHeadingRef.current);
      return;
    }
    markersRef.current[key] = new maptilersdk.Marker({ element: el })
      .setLngLat([pos.lng, pos.lat])
      .addTo(map);
  };

  const removeMarker = (key: string) => {
    markersRef.current[key]?.remove();
    delete markersRef.current[key];
  };

  const ensureRouteLayers = (map: maptilersdk.Map) => {
    if (!map.getSource('go7-route')) {
      map.addSource('go7-route', {
        type: 'geojson',
        data: { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: [] } },
      });
    }
    if (!map.getSource('go7-route-done')) {
      map.addSource('go7-route-done', {
        type: 'geojson',
        data: { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: [] } },
      });
    }
    if (!map.getLayer('go7-route-shadow')) {
      map.addLayer({
        id: 'go7-route-shadow',
        type: 'line',
        source: 'go7-route',
        paint: {
          'line-color': '#000000',
          'line-width': compact ? 10 : 14,
          'line-opacity': 0.22,
        },
      });
    }
    if (!map.getLayer('go7-route-done-line')) {
      map.addLayer({
        id: 'go7-route-done-line',
        type: 'line',
        source: 'go7-route-done',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#9CA3AF',
          'line-width': compact ? 4 : 6,
          'line-opacity': 0.55,
        },
      });
    }
    if (!map.getLayer('go7-route-line')) {
      map.addLayer({
        id: 'go7-route-line',
        type: 'line',
        source: 'go7-route',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#E0001B',
          'line-width': compact ? 5 : 8,
          'line-opacity': 0.95,
        },
      });
    }
  };

  const setLineSource = (sourceId: string, path: LatLng[]) => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    const src = map.getSource(sourceId) as maptilersdk.GeoJSONSource | undefined;
    if (!src) return;
    src.setData({
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: path.length >= 2 ? path.map((p) => [p.lng, p.lat]) : [],
      },
    } as any);
  };

  /** Draw full route, or remaining ahead of rider + faded completed behind */
  const drawRoute = (path: LatLng[], riderPos?: LatLng | null) => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded() || path.length < 2) return;

    ensureRouteLayers(map);

    if (riderPos?.lat && isNavMode) {
      const idx = closestPointIndex(path, riderPos);
      const completed = path.slice(0, Math.max(idx + 1, 1));
      const remaining = path.slice(Math.max(idx, 0));
      setLineSource('go7-route-done', completed.length >= 2 ? completed : []);
      setLineSource('go7-route', remaining.length >= 2 ? remaining : path);

      if (remaining.length >= 2) {
        let meters = 0;
        for (let i = 1; i < remaining.length; i++) {
          meters += distanceMeters(remaining[i - 1], remaining[i]);
        }
        const minutes = Math.max(1, meters / 250);
        onStatsRef.current?.({
          distanceMeters: meters,
          durationMinutes: minutes,
          distanceLabel: formatDistance(meters),
          etaLabel: formatEta(minutes),
        });
      }
      return;
    }

    setLineSource('go7-route-done', []);
    setLineSource('go7-route', path);
  };

  const fitPath = (path: LatLng[]) => {
    const map = mapRef.current;
    if (!map || path.length < 1) return;
    const bounds = new maptilersdk.LngLatBounds(
      [path[0].lng, path[0].lat],
      [path[0].lng, path[0].lat]
    );
    path.forEach((p) => bounds.extend([p.lng, p.lat]));
    map.fitBounds(bounds, {
      padding: isNavMode
        ? { top: 100, bottom: 240, left: 48, right: 48 }
        : 60,
      maxZoom: isNavMode ? 17 : 15,
      duration: 600,
      pitch: isNavMode ? 50 : 0,
    });
  };

  // Static markers (pickup / delivery / nearby) — rider handled in camera loop when navigating
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;

    if (pickupLocation?.lat) {
      upsertMarker('pickup', pickupLocation, makeDotMarkerEl('#E0001B', compact ? 24 : 28));
    } else {
      removeMarker('pickup');
    }

    if (deliveryLocation?.lat) {
      upsertMarker('delivery', deliveryLocation, makeDotMarkerEl('#00B277', compact ? 24 : 28));
    } else {
      removeMarker('delivery');
    }

    if (!isNavMode && currentLocation?.lat) {
      upsertMarker('rider', currentLocation, makeRiderEl(riderHeading));
    } else if (!isNavMode) {
      removeMarker('rider');
    }

    nearbyJobs.forEach((job) => {
      if (!job.lat || !job.lng) return;
      upsertMarker(`job-${job.id}`, job, makeDotMarkerEl('#4285F4', 18));
    });
  }, [
    mapReady,
    pickupLocation?.lat,
    pickupLocation?.lng,
    deliveryLocation?.lat,
    deliveryLocation?.lng,
    currentLocation?.lat,
    currentLocation?.lng,
    riderHeading,
    nearbyJobs,
    compact,
    isNavMode,
  ]);

  // En-route: smooth camera + rider marker + remaining route (does not touch order APIs)
  useEffect(() => {
    if (!mapReady || !isNavMode) return;
    const map = mapRef.current;
    if (!map) return;

    let raf = 0;

    const loop = (ts: number) => {
      const pos = livePosRef.current;
      if (pos?.lat && pos?.lng) {
        if (!cameraStateRef.current) {
          cameraStateRef.current = createCameraState(pos);
        }
        const state = cameraStateRef.current;
        const dt = lastCameraTsRef.current ? ts - lastCameraTsRef.current : 16;
        lastCameraTsRef.current = ts;

        const moving = isMovingRef.current;
        const heading = liveHeadingRef.current || state.heading;
        const t = Math.min(1, (dt / 1000) * (moving ? 5.5 : 3));

        state.lat = state.lat + (pos.lat - state.lat) * t;
        state.lng = state.lng + (pos.lng - state.lng) * t;
        state.heading = lerpHeading(state.heading, heading, moving ? t : t * 0.35);
        state.tilt = state.tilt + (50 - state.tilt) * Math.min(1, t * 0.6);
        const targetZoom = moving ? 17 : 16.2;
        state.zoom = state.zoom + (targetZoom - state.zoom) * Math.min(1, t * 0.4);

        map.jumpTo({
          center: [state.lng, state.lat],
          zoom: state.zoom,
          bearing: state.heading,
          pitch: state.tilt,
        });

        const existing = markersRef.current['rider'];
        if (existing) {
          existing.setLngLat([pos.lng, pos.lat]);
          setRiderHeading(existing.getElement(), state.heading);
        } else {
          markersRef.current['rider'] = new maptilersdk.Marker({
            element: makeRiderEl(state.heading),
          })
            .setLngLat([pos.lng, pos.lat])
            .addTo(map);
        }

        if (routePathRef.current.length > 1 && ts - lastRouteProgressRef.current > 280) {
          lastRouteProgressRef.current = ts;
          drawRoute(routePathRef.current, pos);
        }
      }
      raf = requestAnimationFrame(loop);
    };

    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable nav loop
  }, [mapReady, isNavMode]);

  // Home / overview follow (non-nav)
  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map || !currentLocation?.lat || isNavMode || !followRider) return;
    map.easeTo({
      center: [currentLocation.lng, currentLocation.lat],
      duration: 500,
    });
  }, [mapReady, isNavMode, followRider, currentLocation?.lat, currentLocation?.lng]);

  // Overview route: pickup → delivery
  useEffect(() => {
    if (!mapReady || !showRoute || isNavMode) return;
    if (!pickupLocation?.lat || !deliveryLocation?.lat) return;

    let cancelled = false;
    void fetchInAppRoute(pickupLocation, deliveryLocation).then((route) => {
      if (cancelled) return;
      routePathRef.current = route.path;
      drawRoute(route.path);
      fitPath(route.path);
      onRouteStatsUpdate?.({
        distanceMeters: route.distanceMeters,
        durationMinutes: route.durationMinutes,
        distanceLabel: route.distanceLabel,
        etaLabel: route.etaLabel,
      });
    });

    return () => {
      cancelled = true;
    };
  }, [
    mapReady,
    isNavMode,
    showRoute,
    pickupLocation?.lat,
    pickupLocation?.lng,
    deliveryLocation?.lat,
    deliveryLocation?.lng,
  ]);

  // Navigation route: rider → navTarget (pickup then delivery). Refetch only on move/dest change.
  useEffect(() => {
    if (!mapReady || !showRoute || !isNavMode) return;

    const destination =
      navTarget?.lat && navTarget?.lng ? navTarget : deliveryLocation;
    if (!destination?.lat) return;

    const destKey = `${destination.lat.toFixed(4)},${destination.lng.toFixed(4)}`;
    const origin =
      livePosRef.current ||
      (currentLocation?.lat ? currentLocation : null) ||
      pickupLocation;
    if (!origin?.lat) {
      if (pickupLocation?.lat && destination.lat) {
        void fetchInAppRoute(pickupLocation, destination, { force: true }).then((route) => {
          routePathRef.current = route.path;
          drawRoute(route.path);
          fitPath(route.path);
        });
      }
      return;
    }

    const destChanged = destKey !== navDestKeyRef.current;
    const shouldFetch = shouldRefetchInAppRoute({
      lastOrigin: lastNavOriginRef.current,
      nextOrigin: origin,
      lastFetchedAt: lastNavFetchAtRef.current,
      hasPath: routePathRef.current.length > 1,
      destinationChanged: destChanged,
    });

    if (!shouldFetch) {
      drawRoute(routePathRef.current, livePosRef.current || origin);
      return;
    }

    if (destChanged) {
      navDestKeyRef.current = destKey;
      routePathRef.current = [];
      lastNavOriginRef.current = null;
    }

    let cancelled = false;
    lastNavOriginRef.current = origin;
    lastNavFetchAtRef.current = Date.now();

    void fetchInAppRoute(origin, destination, { force: destChanged }).then((route) => {
      if (cancelled) return;
      routePathRef.current = route.path;
      const rider = livePosRef.current || origin;
      drawRoute(route.path, rider);
      if (destChanged || !hasRiderGps) {
        fitPath([origin, ...route.path, destination]);
      }
      onRouteStatsUpdate?.({
        distanceMeters: route.distanceMeters,
        durationMinutes: route.durationMinutes,
        distanceLabel: route.distanceLabel,
        etaLabel: route.etaLabel,
      });
    });

    return () => {
      cancelled = true;
    };
  }, [
    mapReady,
    isNavMode,
    showRoute,
    navTarget?.lat,
    navTarget?.lng,
    deliveryLocation?.lat,
    deliveryLocation?.lng,
    pickupLocation?.lat,
    pickupLocation?.lng,
    currentLocation?.lat,
    currentLocation?.lng,
    hasRiderGps,
  ]);

  if (loadError) {
    return (
      <div className="w-full h-full bg-gray-100 flex flex-col items-center justify-center gap-2 text-center px-4">
        <MapPin className="w-8 h-8 text-[#6B6B6B]" />
        <p className="text-[14px] text-[#111111] font-medium">Map unavailable</p>
        <p className="text-[12px] text-[#6B6B6B]">Check connection and try again</p>
      </div>
    );
  }

  return (
    <div
      className={
        isNavMode
          ? 'absolute inset-0 w-full h-full min-h-0'
          : 'relative w-full h-full min-h-0'
      }
    >
      <div ref={containerRef} className="absolute inset-0 w-full h-full" />
      {!mapReady && (
        <div className="absolute inset-0 bg-[#f3f4f6] flex items-center justify-center z-10">
          <Loader2 className="w-8 h-8 text-[#E0001B] animate-spin" />
        </div>
      )}
    </div>
  );
}
